import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiInstagram, FiYoutube, FiLinkedin } = FiIcons;

export default function HeroSection() {
  const videoRef = useRef(null);
  const fadeState = useRef('uninitialized');

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const animateOpacity = (start, end, duration) => {
      const startTime = performance.now();
      const animate = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const currentOpacity = start + (end - start) * progress;
        
        if (videoRef.current) {
          videoRef.current.style.opacity = currentOpacity.toString();
        }
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      requestAnimationFrame(animate);
    };

    const handleCanPlay = () => {
      if (fadeState.current !== 'uninitialized') return;
      fadeState.current = 'playing';
      video.play().catch(() => {});
      animateOpacity(0, 1, 500);
    };

    const handleTimeUpdate = () => {
      const remaining = video.duration - video.currentTime;
      if (remaining <= 0.55 && fadeState.current === 'playing') {
        fadeState.current = 'fading-out';
        const currentOp = parseFloat(video.style.opacity || '1');
        animateOpacity(currentOp, 0, 500);
      }
    };

    const handleEnded = () => {
      fadeState.current = 'ended';
      video.style.opacity = '0';
      
      setTimeout(() => {
        video.currentTime = 0;
        video.play().catch(() => {});
        fadeState.current = 'playing';
        animateOpacity(0, 1, 500);
      }, 100);
    };

    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('ended', handleEnded);

    return () => {
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('ended', handleEnded);
    };
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col overflow-hidden">
      {/* Background Video */}
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-cover object-bottom opacity-0"
        muted
        playsInline
        preload="auto"
        src="https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260405_074625_a81f018a-956b-43fb-9aee-4d1508e30e6a.mp4"
      />

      {/* Navbar */}
      <nav className="relative z-20 px-6 py-6 w-full">
        <div className="liquid-glass rounded-full max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center">
            <div className="w-6 h-6 mr-3 flex items-center justify-center shrink-0">
              <img 
                src="https://media-manager-c.questera.ai/greta-media/b729ed0728dd00b56ad4432b7bed08bb6e0f1b37b57acc2ecba8e8caa91ad92afaa368f7ee662627c72dbcd9a47643b4/images/aW1hZ2UvcG5n/cd6c64635923cfb3391950ed67895587.png" 
                alt="Logo" 
                className="w-full h-full object-contain"
              />
            </div>
            <span className="text-white font-semibold text-lg uppercase tracking-wider">SANS TITRE</span>
            <div className="hidden md:flex items-center gap-8 ml-8 text-white/80 text-sm font-medium">
              <Link to="/brand-report" className="hover:text-white transition-colors">Brand Report</Link>
              <Link to="/portfolio" className="hover:text-white transition-colors">Portfolio</Link>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/contact" className="text-white text-sm font-medium hidden md:block hover:text-white/80 transition-colors">Connect</Link>
            <Link to="/outpost" className="liquid-glass rounded-full px-6 py-2 text-white text-sm font-medium hover:bg-white/5 transition-colors block">
              THE OUTPOST
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Content */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 py-12 text-center md:-translate-y-[10%]">
        
        <h1 className="text-5xl md:text-7xl lg:text-8xl text-white tracking-tight font-serif mb-10 w-full max-w-6xl leading-[1.1]">
          For Those Who See <br className="hidden md:block" />
          What Isn't There <em className="italic text-white/90">Yet.</em>
        </h1>

        <p className="text-white text-sm md:text-base leading-relaxed px-4 max-w-md mb-8 opacity-80 font-light mt-4">
          Sans Titre is an independent creative studio shaping brands, content, and digital experiences.
        </p>

        <Link 
          to="/contact" 
          className="inline-block liquid-glass rounded-full px-8 py-3 text-white text-sm font-medium hover:bg-white/5 transition-colors"
        >
          CONTACT US
        </Link>
      </div>

      {/* Social icons footer */}
      <div className="relative z-10 flex justify-center gap-4 pb-12">
        {[
          { Icon: FiInstagram, url: 'https://www.instagram.com/wearesanstitre.co/' },
          { Icon: FiYoutube, url: 'https://www.youtube.com/@sanstitreco' },
          { Icon: FiLinkedin, url: 'https://www.linkedin.com/company/sans-titre-co/' }
        ].map(({ Icon, url }, idx) => (
          <a 
            key={idx} 
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="liquid-glass rounded-full p-4 text-white/80 hover:text-white hover:bg-white/5 transition-all"
          >
            <SafeIcon icon={Icon} className="w-5 h-5" />
          </a>
        ))}
      </div>
    </section>
  );
}